const DEFAULT_HARD_DELETE_DAYS = 30;
// Same idea as ConfirmationService.sliceMessageText: long titles must not push the popup past its fixed height
const MAX_TITLE_LENGTH = 30;

/** `customClass` of the confirmation popup — styled in `src/scss/confirmation-dialog.scss`. */
export const DISABLE_WORKSPACE_RETENTION_STYLE_CLASS = 'disable-workspace-retention';

export interface ConfirmationTextPart {
  text: string;
  /** Rendered in the accent colour like the entity name in every other confirmation popup. */
  highlight?: boolean;
}

/**
 * Message parts for the confirmation shown when an admin turns a workspace's "Long-term retention
 * by default" OFF (SNAP-2246). Returns null when nothing needs confirming.
 *
 * The backend restarts the hard-delete countdown for every recording in the workspace from the
 * moment the setting is disabled, so the admin has `hardDeleteDays` to retain them individually.
 */
export const describeDisableWorkspaceRetention = ({
  workspaceTitle,
  protectedCount,
  hardDeleteDays,
}: {
  workspaceTitle: string;
  protectedCount: number;
  hardDeleteDays: number | null | undefined;
}): ConfirmationTextPart[] | null => {
  if (!protectedCount) return null;

  const days = hardDeleteDays || DEFAULT_HARD_DELETE_DAYS;
  const recordings = `${protectedCount} recording${protectedCount === 1 ? '' : 's'}`;
  const title =
    workspaceTitle.length > MAX_TITLE_LENGTH ? `${workspaceTitle.slice(0, MAX_TITLE_LENGTH)}…` : workspaceTitle;

  // Kept to ~5 lines at the popup's 18px / 262px message box, like the other confirmations
  return [
    { text: 'Turn off default retention for ' },
    { text: `"${title}"`, highlight: true },
    { text: '? ' },
    { text: recordings, highlight: true },
    { text: ` will be auto-deleted in ${days} days unless retained.` },
  ];
};

/** Plain-text form of the parts, for logs and tests. */
export const joinConfirmationText = (parts: ConfirmationTextPart[]): string => parts.map((p) => p.text).join('');
